import React, { useEffect, useState } from "react";
import BillingHeader from "../Billing/BillingHeader";
import BillingFilter from "../Billing/BillingFilter";
import Typography from "../../Utility/Typography";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import Button from "../../Utility/Button/Button";
import { RiSearchLine } from "react-icons/ri";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import { useDispatch, useSelector } from "react-redux";
import {
  selectClientQbMappingData,
  selectClientQbMappingError,
  selectClientQbMappingLoading,
  updateClientQbMapping,
} from "../../Redux/Slices/clientQbMappingSlice";
import { fetchClientQbMappings } from "../../Redux/Thunks/clientQbMappingThunk";

const QB_CLIENT_OPTIONS = [
  "Acme Corp",
  "Zenith Tech",
  "Rivera Design LLC",
  "Stellar Solutions Inc",
  "HealthCo Partners",
  "Global Logistics",
];

const ClientQBMapping = () => {
  /* ===================== FILTER STATE ===================== */
  const [filters, setFilters] = useState({
    client: "",
    qbClient: "",
  });

  /* ===================== REDUX STATE ===================== */
  const dispatch = useDispatch();
  const clientQbData = useSelector(selectClientQbMappingData);
  const loading = useSelector(selectClientQbMappingLoading);
  const error = useSelector(selectClientQbMappingError);

  const rows = clientQbData?.list || [];

  /* ===================== FETCH DATA ON MOUNT ===================== */
  useEffect(() => {
    if (!clientQbData && !loading) {
      dispatch(fetchClientQbMappings());
    }
  }, [dispatch, clientQbData, loading]);

  /* ===================== CLIENT OPTIONS ===================== */
  // Extract unique client names from rows and add "All Clients" as first option
  const clientOptions = [
    "All Clients",
    ...Array.from(new Set(rows.map((row) => row.client))).sort(),
  ];

  /* ===================== FILTERED DATA ===================== */
  const filteredRows = rows.filter((row) => {
    // Filter by client
    if (filters.client && filters.client !== "All Clients") {
      if (row.client !== filters.client) return false;
    }

    // Filter by qbClient
    if (filters.qbClient && filters.qbClient !== "Search QuickBooks Client") {
      if (!row.qbClient || row.qbClient !== filters.qbClient) return false;
    }

    return true;
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMapQBClient = (id, qbClient) => {
    dispatch(updateClientQbMapping({ id, qbClient }));
  };

  /* ===================== SORTING ===================== */
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredRows, {
    defaultSortKey: "client",
    defaultSortDirection: "asc",
  });

  return (
    <div>
      {/* ===================== HEADER ===================== */}
      <BillingHeader
        title="Client-Level QuickBooks Mapping"
        subtitle="Map internal clients to QuickBooks client names."
      />

      {/* ===================== FILTER ===================== */}
      <BillingFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        clientOptions={clientOptions}
        qbClientOptions={QB_CLIENT_OPTIONS}
        addMappingButton={false}
        notShowTag={false}
        notShowAccount={false}
        notShowUser={false}
        notShowAction={false}
      />

      {/* ===================== TABLE ===================== */}
      <div className="w-full">
        <div className="relative flex flex-col bg-surface-primary shadow-sm">
          <div className="overflow-x-auto">
            {loading && (
              <div className="py-6 text-center">
                <Typography className="text-text-secondary">
                  Loading client QuickBooks mappings...
                </Typography>
              </div>
            )}
            {error && !loading && (
              <div className="py-6 text-center">
                <Typography className="text-red-500">{error}</Typography>
              </div>
            )}
            <table className="w-full clients-table">
              {/* TABLE HEADER */}
              <thead className="bg-surface-secondary">
                <tr>
                  <SortableHeader
                    columnKey="client"
                    label="Internal Client"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />

                  <SortableHeader
                    columnKey="qbClient"
                    label="QuickBooks Client"
                    onSort={handleSort}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                  />

                  <SortableHeader columnKey="action" label="" sortable={false} />
                </tr>
              </thead>

              {/* TABLE BODY */}
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-6 px-4">
                      <Typography className="text-text-secondary">
                        No clients found matching the filters
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id}>
                      {/* INTERNAL CLIENT */}
                      <td className="px-4 py-3">
                        <Typography className="font-medium">{row.client}</Typography>
                      </td>

                      {/* QB CLIENT */}
                      <td className="px-4 py-3">
                        {row.qbClient ? (
                          <div className="flex items-center gap-3">
                            <Typography>{row.qbClient}</Typography>

                            <Typography
                              variant="span1"
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-700"
                            >
                              ✓ Mapped
                            </Typography>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Typography variant="span1" className="text-text-secondary text-sm">
                              Unmapped
                            </Typography>

                            <div className="min-w-[260px]">
                              <Dropdown
                                placeholder="Add QuickBooks Name..."
                                options={QB_CLIENT_OPTIONS}
                                searchable
                                noWrapper
                                onChange={(e) => handleMapQBClient(row.id, e.target.value)}
                              />
                            </div>

                            <Button
                              size="sm"
                              className="!w-8 !h-8 !p-0 flex items-center justify-center"
                              title="Search"
                            >
                              <RiSearchLine />
                            </Button>
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-3"></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ===================== FOOTER ===================== */}
          <div className="flex justify-end items-center gap-3 px-4 py-3 border-t bg-surface-secondary">
            <Button variant="" className="btn-reset">
              Cancel
            </Button>
            <Button variant="primary">Save Configuration</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientQBMapping;
